/**
 * FamilyStrip — Redesign v2 (hero-led product home).
 *
 * Horizontal row of circular profile avatars with a two-line caption
 * underneath each (primary = name / "Me", secondary = role — "My Chart",
 * "Child", "Spouse", "Family"). The active profile gets a prominent gold
 * ring and a soft gold glow; inactive profiles use a subtle white hairline.
 * The "Add" slot uses a dashed gold ring with a plus icon.
 *
 * This component replaces the older "NJ · Me ▾" ProfileChip because it
 * communicates the multi-profile astrology use-case (family kundalis)
 * that is central to JyotishPuraan — a generic chip can't do that.
 *
 * Design-system rules enforced:
 *   - Sits on the void with NO container card — purely circular shapes
 *     floating on the page so the hero remains the dominant surface.
 *   - Only the active ring uses gold; inactive use 7 % white hairline.
 *   - Label primary: Inter 12 px, 100 % white for active / 60 % otherwise.
 *   - Label secondary: Inter 10 px, 40 % white (ghost).
 *
 * Props:
 *   selfName          : string   — the signed-in user's name.
 *   familyProfiles    : array    — [{profile_id, name, relation_type, …}].
 *   activeProfile     : object | null — null means "Me" is active.
 *   onSelect          : (profile|null) => void
 *   onAddNew?         : () => void
 */
import React from 'react';
import { Plus } from 'lucide-react';

function initialsOf(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] || '';
  const last  = parts.length > 1 ? (parts[parts.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

// Prettify relation_type (e.g. "self" → "My Chart", "child" → "Child").
function relationLabel(relation) {
  if (!relation) return 'Family';
  const r = String(relation).toLowerCase();
  if (r === 'self')   return 'My Chart';
  // Capitalise first letter; spouse/father/mother/child/sibling/friend/other
  return r.charAt(0).toUpperCase() + r.slice(1);
}

const RING_SIZE = 54;   // overall circle diameter
const RING_INNER_BG_ACTIVE   = 'var(--card-glass)';
const RING_INNER_BG_DEFAULT  = 'var(--card-glass)';

const Avatar = ({ children, active, label, sublabel, onClick, testId, dashed }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="cv-font-ui"
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: '4px 2px 2px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      flexShrink: 0,
      width: '74px',
    }}
  >
    {/* Outer ring */}
    <span
      aria-hidden="true"
      style={{
        width:  `${RING_SIZE}px`,
        height: `${RING_SIZE}px`,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? RING_INNER_BG_ACTIVE : RING_INNER_BG_DEFAULT,
        border: dashed
          ? '1.5px dashed rgba(212,164,24,0.45)'
          : active
            ? '2px solid var(--cv-gold-primary)'
            : '1px solid rgba(255,255,255,0.10)',
        boxShadow: 'none',
        color: active ? 'var(--cv-gold-primary)' : 'var(--cv-text-body)',
        fontSize: '16px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        transition: 'box-shadow 220ms ease, background 220ms ease',
      }}
    >
      {children}
    </span>

    {/* Primary label */}
    <span
      style={{
        fontSize: '12px',
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--cv-gold-primary)' : 'var(--cv-text-body)',
        maxWidth: '72px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 1,
      }}
    >
      {label}
    </span>

    {/* Secondary label — relation */}
    {sublabel && (
      <span
        style={{
          fontSize: '10px',
          color: 'var(--cv-text-ghost)',
          letterSpacing: '0.02em',
          marginTop: '-4px',
          lineHeight: 1,
        }}
      >
        {sublabel}
      </span>
    )}
  </button>
);

const FamilyStrip = ({
  selfName,
  familyProfiles = [],
  activeProfile,
  onSelect,
  onAddNew,
}) => {
  const isSelfActive = !activeProfile;

  return (
    <div
      className="cv-enter"
      data-testid="family-strip"
      style={{ padding: '2px 0' }}
    >
      <div
        className="cv-hscroll"
        style={{
          gap: '18px',
          WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)',
          maskImage:       'linear-gradient(to right, black 90%, transparent 100%)',
        }}
      >
        {/* Me */}
        <Avatar
          active={isSelfActive}
          label="Me"
          sublabel="My Chart"
          onClick={() => onSelect(null)}
          testId="family-strip-me"
        >
          {initialsOf(selfName) || 'Me'}
        </Avatar>

        {/* Family members (seeker-mode only — already filtered upstream to
            exclude astrologer 'client' relation) */}
        {familyProfiles.map((p) => {
          const active = activeProfile?.profile_id === p.profile_id;
          const firstName = p.name?.split(' ')[0] || 'Profile';
          return (
            <Avatar
              key={p.profile_id}
              active={active}
              label={firstName}
              sublabel={relationLabel(p.relation_type)}
              onClick={() => onSelect(p)}
              testId={`family-strip-${p.profile_id}`}
            >
              {initialsOf(p.name)}
            </Avatar>
          );
        })}

        {/* Add */}
        {onAddNew && (
          <Avatar
            dashed
            label="Add"
            sublabel="Family"
            onClick={onAddNew}
            testId="family-strip-add-btn"
          >
            <Plus size={18} strokeWidth={1.75} style={{ color: 'var(--cv-gold-primary)' }} />
          </Avatar>
        )}
      </div>
    </div>
  );
};

export default FamilyStrip;
